import React, { useEffect, useRef, useState } from "react";
import dashboardData from "../data/private/dashboard.json";
import profileData from "../data/private/settings.profile.json";
import notificationsData from "../data/private/settings.notifications.json";
import securityData from "../data/private/settings.security.json";
import { logActivity } from "../utils/activity";
import InfoBanner from "../components/InfoBanner";
import {
  changePassword,
  getNotificationSettings,
  getProfileSettings,
  updateNotificationSettings,
  updateProfileSettings,
} from "../api/settings.api";

export default function DashboardSettings({ initialTabLabel = profileData.tabLabel }) {
  const [activeTab, setActiveTab] = useState(initialTabLabel);
  const [profile, setProfile] = useState({
    display_name: "",
    language: "",
    timezone: "",
    usage_intent: "",
    experience_level: "",
    primary_interest: "",
    profile_photo_url: "",
  });
  const [notify, setNotify] = useState({
    email: true,
    whatsapp: false,
    report: true,
    marketing: false,
  });
  const [toast, setToast] = useState("");
  const [securityToast, setSecurityToast] = useState("");
  const [loadError, setLoadError] = useState("");
  const [notificationsReady, setNotificationsReady] = useState(true);
  const [passwordReady, setPasswordReady] = useState(true);
  const [passwordForm, setPasswordForm] = useState({
    current: "",
    new: "",
  });
  const profileRequestedRef = useRef(false);
  const notifyRequestedRef = useRef(false);

  useEffect(() => {
    if (profileRequestedRef.current) return;
    profileRequestedRef.current = true;
    const fetchProfile = async () => {
      try {
        const data = await getProfileSettings();
        const next = data || {};
        setProfile({
          display_name: next.display_name ?? "",
          language: next.language ?? "",
          timezone: next.timezone ?? "",
          usage_intent: next.usage_intent ?? "",
          experience_level: next.experience_level ?? "",
          primary_interest: next.primary_interest ?? "",
          profile_photo_url: next.profile_photo_url ?? "",
        });
        setLoadError("");
      } catch (error) {
        setLoadError(error?.response?.data?.message || error?.message || "");
        logActivity("api_error", { module: "settings_profile" });
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    if (notifyRequestedRef.current) return;
    notifyRequestedRef.current = true;
    const fetchNotifications = async () => {
      try {
        const data = await getNotificationSettings();
        const next = data || {};
        setNotify((prev) => ({
          email: typeof next.email === "boolean" ? next.email : prev.email,
          whatsapp:
            typeof next.whatsapp === "boolean" ? next.whatsapp : prev.whatsapp,
          report: typeof next.report === "boolean" ? next.report : prev.report,
          marketing:
            typeof next.marketing === "boolean"
              ? next.marketing
              : prev.marketing,
        }));
        setNotificationsReady(true);
        setLoadError("");
      } catch (error) {
        if (error?.response?.status === 500 && !error?.response?.data) {
          setNotificationsReady(false);
          setLoadError("Data unavailable.");
          return;
        }
        if (error?.response?.status === 501) {
          setNotificationsReady(false);
          setLoadError("");
          return;
        }
        setLoadError(error?.response?.data?.message || error?.message || "");
        logActivity("api_error", { module: "settings_notifications" });
      }
    };
    fetchNotifications();
  }, []);

  const handleProfileSave = async () => {
    setToast("");
    try {
      const payload = {
        display_name: profile.display_name,
        language: profile.language,
        timezone: profile.timezone,
        usage_intent: profile.usage_intent,
        experience_level: profile.experience_level,
        primary_interest: profile.primary_interest,
        profile_photo_url: profile.profile_photo_url,
      };
      await updateProfileSettings(payload);
      setToast(profileData.toastMessage);
      logActivity("profile_updated");
    } catch (error) {
      setToast(error?.response?.data?.message || error?.message || "");
      logActivity("api_error", { module: "settings_profile" });
    }
  };

  const handleToggle = async (key) => {
    if (!notificationsReady) {
      return;
    }
    const next = { ...notify, [key]: !notify[key] };
    setNotify(next);
    setToast("");
    try {
      await updateNotificationSettings(next);
      setToast(notificationsData.toastMessage);
      logActivity("notification_toggle", { key });
    } catch (error) {
      setToast(error?.response?.data?.message || error?.message || "");
      logActivity("api_error", { module: "settings_notifications" });
    }
  };

  const handleSecurityAction = async (label, toastMessage) => {
    if (label === securityData.changePassword.title) {
      setSecurityToast("");
      try {
        await changePassword({
          currentPassword: passwordForm.current,
          newPassword: passwordForm.new,
        });
        setPasswordReady(true);
        setSecurityToast(securityData.changePassword.toastMessage);
        logActivity("security_action", { label });
        setPasswordForm({ current: "", new: "" });
      } catch (error) {
        if (error?.response?.status === 500 && !error?.response?.data) {
          setPasswordReady(false);
          setSecurityToast("Data unavailable.");
          return;
        }
        if (error?.response?.status === 501) {
          setPasswordReady(false);
          setSecurityToast("Coming soon.");
          return;
        }
        setSecurityToast(error?.response?.data?.message || error?.message || "");
        logActivity("api_error", { module: "settings_security" });
      }
      return;
    }
    setSecurityToast(toastMessage);
    logActivity("security_action", { label });
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-slate-900">
        {dashboardData.settings.title}
      </h1>
      <div className="flex flex-wrap gap-2">
        {[profileData, notificationsData, securityData].map((tab) => (
          <button
            key={tab.tabLabel}
            className={`btn ${
              activeTab === tab.tabLabel ? "btn-primary" : "btn-secondary"
            }`}
            onClick={() => setActiveTab(tab.tabLabel)}
          >
            {tab.tabLabel}
          </button>
        ))}
      </div>

      {activeTab === profileData.tabLabel && (
        <div className="card p-6">
          <p className="text-sm font-semibold text-slate-900">
            {profileData.title}
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {profileData.fields.map((field) => (
              <div key={field.key}>
                <label className="text-xs text-slate-500">{field.label}</label>
                <input
                  className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  placeholder={field.placeholder}
                  value={profile[field.key]}
                  readOnly={field.readOnly}
                  onChange={(event) =>
                    setProfile((prev) => ({
                      ...prev,
                      [field.key]: event.target.value,
                    }))
                  }
                />
              </div>
            ))}
          </div>
          <button className="btn btn-primary mt-4" onClick={handleProfileSave}>
            {profileData.saveLabel}
          </button>
          {toast && <p className="mt-2 text-xs text-slate-500">{toast}</p>}
          {loadError && <p className="mt-2 text-xs text-rose-500">{loadError}</p>}
        </div>
      )}

      {activeTab === notificationsData.tabLabel && (
        <div className="card p-6">
          <p className="text-sm font-semibold text-slate-900">
            {notificationsData.title}
          </p>
          {!notificationsReady && (
            <div className="mt-3">
              <InfoBanner
                title="Notifications coming soon"
                description="Notifications are not available yet."
              />
            </div>
          )}
          <div className="mt-4 space-y-3">
            {notificationsData.toggles.map((toggle) => (
              <label
                key={toggle.key}
                className="flex items-center justify-between text-sm text-slate-600"
              >
                {toggle.label}
                <input
                  type="checkbox"
                  checked={Boolean(notify[toggle.key])}
                  onChange={() => handleToggle(toggle.key)}
                  disabled={!notificationsReady}
                />
              </label>
            ))}
          </div>
          {toast && <p className="mt-2 text-xs text-slate-500">{toast}</p>}
        </div>
      )}

      {activeTab === securityData.tabLabel && (
        <div className="card p-6">
          <p className="text-sm font-semibold text-slate-900">
            {securityData.title}
          </p>
          {!passwordReady && (
            <div className="mt-3">
              <InfoBanner
                title="Password change coming soon"
                description="Password updates are not available yet."
              />
            </div>
          )}
          <div className="mt-3 text-sm text-slate-600">
            <p>
              {securityData.lastLoginLabel}: {securityData.lastLoginValue}
            </p>
            <p className="mt-1">
              {securityData.sessionExpiryLabel}: {securityData.sessionExpiryValue}
            </p>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {securityData.changePassword.fields.map((field) => (
              <div key={field.key}>
                <label className="text-xs text-slate-500">{field.label}</label>
                <input
                  className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  placeholder={field.placeholder}
                  value={passwordForm[field.key]}
                  onChange={(event) =>
                    setPasswordForm((prev) => ({
                      ...prev,
                      [field.key]: event.target.value,
                    }))
                  }
                />
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              className="btn btn-primary"
              onClick={() =>
                handleSecurityAction(
                  securityData.changePassword.title,
                  securityData.changePassword.toastMessage
                )
              }
              disabled={!passwordReady}
            >
              {securityData.changePassword.submitLabel}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() =>
                handleSecurityAction(
                  securityData.logoutAll.label,
                  securityData.logoutAll.toastMessage
                )
              }
            >
              {securityData.logoutAll.label}
            </button>
          </div>
          {securityToast && (
            <p className="mt-2 text-xs text-slate-500">{securityToast}</p>
          )}
        </div>
      )}
    </div>
  );
}
